import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  User,
  MapPin,
  Building2,
  CheckCircle2,
  Save,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// HR-managed fields that require confirmation
const HR_MANAGED_FIELDS = [
  'first_name', 'last_name', 'date_of_birth', 'address', 'city', 'state', 'zip_code',
  'hire_date', 'job_title', 'department', 'employment_status', 'annual_salary', 'ssn_last_four'
];

// Field status indicator component
const FieldStatusIndicator = ({ fieldName, status }) => {
  if (!HR_MANAGED_FIELDS.includes(fieldName)) return null;
  
  if (status === 'confirmed') {
    return (
      <div className="flex items-center gap-1 text-emerald-600 text-sm">
        <CheckCircle2 className="w-4 h-4" />
        <span>Confirmed</span>
      </div>
    );
  } else if (status === 'pending') {
    return (
      <div className="flex items-center gap-1 text-amber-600 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>Pending HR Review</span>
      </div>
    );
  } else if (status === 'denied') {
    return (
      <div className="flex items-center gap-1 text-red-600 text-sm">
        <X className="w-4 h-4" />
        <span>Denied</span>
      </div>
    );
  }
  
  return null;
};

export default function EmployeeProfile() {
  const [employee, setEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldStatus, setFieldStatus] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      
      // Check if user has an employee_id first
      if (!user.employee_id) {
        setLoading(false);
        return;
      }
      
      const employees = await api.entities.Employee.filter({ id: user.employee_id });
      
      if (employees.length > 0) {
        const employeeData = employees[0];
        
        // Convert ISO date strings to YYYY-MM-DD format for date inputs
        if (employeeData.date_of_birth) {
          employeeData.date_of_birth = employeeData.date_of_birth.split('T')[0];
        }
        if (employeeData.hire_date) {
          employeeData.hire_date = employeeData.hire_date.split('T')[0];
        }
        
        // Set field confirmation status
        setFieldStatus(employeeData.field_confirmation_status || {});
        
        setEmployee(employeeData);
        setFormData(employeeData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Check if critical info changed
      const criticalFields = ['first_name', 'last_name', 'date_of_birth', 'address', 'city', 'state', 'zip_code'];
      const hasChanges = criticalFields.some(field => formData[field] !== employee[field]);
      
      await api.entities.Employee.update(employee.id, formData);
      
      // Notify HR if critical information changed
      if (hasChanges) {
        try {
          const employer = await api.entities.Employer.filter({ id: employee.employer_id });
          if (employer.length > 0 && employer[0].hr_contact_email) {
            const changes = criticalFields
              .filter(field => formData[field] !== employee[field])
              .map(field => `${field}: "${employee[field]}" â†’ "${formData[field]}"`)
              .join(', ');

            await api.integrations.Core.SendEmail({
              to: employer[0].hr_contact_email,
              subject: `Employee Profile Update - ${employee.first_name} ${employee.last_name}`,
              body: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                  <h2 style="color: #10b981;">Employee Profile Update Notification</h2>
                  <p>An employee has updated their profile information:</p>
                  <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
                    <p><strong>Employee:</strong> ${employee.first_name} ${employee.last_name}</p>
                    <p><strong>Email:</strong> ${employee.user_email}</p>
                    <p><strong>Changes Made:</strong></p>
                    <p style="font-size: 14px; color: #64748b;">${changes}</p>
                  </div>
                  <p style="font-size: 14px; color: #64748b;">Please review these changes and update any relevant records.</p>
                </div>
              `
            });
          }
        } catch (emailErr) {
          console.error('Failed to notify HR:', emailErr);
        }
      }
      
      // Check which HR-managed fields changed
      const changedHRFields = HR_MANAGED_FIELDS.filter(field => formData[field] !== employee[field]);
      
      const updatedEmployee = await api.entities.Employee.update(employee.id, formData);
      
      // Update field status from response
      if (updatedEmployee.field_confirmation_status) {
        setFieldStatus(updatedEmployee.field_confirmation_status);
      }
      
      // Update local employee data
      if (updatedEmployee.date_of_birth) {
        updatedEmployee.date_of_birth = updatedEmployee.date_of_birth.split('T')[0];
      }
      if (updatedEmployee.hire_date) {
        updatedEmployee.hire_date = updatedEmployee.hire_date.split('T')[0];
      }
      
      setEmployee(updatedEmployee);
      setFormData(updatedEmployee);
      
      // Show appropriate message based on whether HR review is needed
      if (changedHRFields.length > 0) {
        toast.success('Profile updated! Changes to HR-managed fields are pending approval.', {
          duration: 5000,
        });
      } else {
        toast.success('Profile updated successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const navigate = useNavigate();

  const handleConfirmProfile = async () => {
    // Validate required fields for accurate pricing
    const requiredFields = ['date_of_birth', 'gender'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields (Date of Birth and Gender) for accurate benefit pricing.');
      return;
    }
    
    setSaving(true);
    try {
      await api.entities.Employee.update(employee.id, { 
        ...formData, 
        profile_confirmed: true 
      });
      setEmployee({ ...employee, ...formData, profile_confirmed: true });
      setFormData(prev => ({ ...prev, profile_confirmed: true }));
      toast.success('Profile confirmed! Let\'s explore your benefits.');
      
      // Redirect to intro page after confirmation
      setTimeout(() => {
        navigate(createPageUrl('EnrollmentIntro'));
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to confirm profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-16">
        <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-700 mb-2">No Employee Profile Found</h3>
        <p className="text-slate-500 mb-6">
          Your account does not have an employee profile linked.
        </p>
        <Button 
          variant="outline"
          onClick={() => window.location.href = createPageUrl('AccountSettings')}
        >
          Go to Account Settings
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Profile</h1>
          <p className="text-slate-500 mt-1">
            Review and update your personal information
          </p>
        </div>
        {formData.profile_confirmed ? (
          <Badge className="bg-emerald-100 text-emerald-700 h-8">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Confirmed
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 h-8">
            <AlertCircle className="w-4 h-4 mr-1" />
            Pending Confirmation
          </Badge>
        )}
      </div>

      {/* Confirmation Banner */}
      {!formData.profile_confirmed && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">Please Confirm Your Information</p>
                <p className="text-sm text-amber-700">Review all your details and confirm to proceed with benefits enrollment.</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Personal Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-500" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="first_name">First Name</Label>
                  <FieldStatusIndicator fieldName="first_name" status={fieldStatus.first_name} />
                </div>
                <Input
                  id="first_name"
                  value={formData.first_name || ''}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="last_name">Last Name</Label>
                  <FieldStatusIndicator fieldName="last_name" status={fieldStatus.last_name} />
                </div>
                <Input
                  id="last_name"
                  value={formData.last_name || ''}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <FieldStatusIndicator fieldName="date_of_birth" status={fieldStatus.date_of_birth} />
                </div>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={(e) => handleChange('date_of_birth', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ssn_last_four">SSN (Last 4 digits)</Label>
                  <FieldStatusIndicator fieldName="ssn_last_four" status={fieldStatus.ssn_last_four} />
                </div>
                <Input
                  id="ssn_last_four"
                  maxLength={4}
                  value={formData.ssn_last_four || ''}
                  onChange={(e) => handleChange('ssn_last_four', e.target.value)}
                  placeholder="XXXX"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="gender">
                  Gender <span className="text-red-500">*</span>
                  <span className="text-xs text-slate-500 ml-1">(Required for pricing)</span>
                </Label>
                <Select 
                  value={formData.gender || ''} 
                  onValueChange={(v) => handleChange('gender', v)}
                >
                  <SelectTrigger className={!formData.gender ? 'border-amber-400' : ''}>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tobacco_user">
                  Tobacco User <span className="text-xs text-slate-500">(Affects pricing)</span>
                </Label>
                <Select 
                  value={formData.tobacco_user ? 'true' : 'false'} 
                  onValueChange={(v) => handleChange('tobacco_user', v === 'true')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user_email">Email</Label>
                <Input
                  id="user_email"
                  type="email"
                  value={formData.user_email || ''}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Address Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="address">Street Address</Label>
                <FieldStatusIndicator fieldName="address" status={fieldStatus.address} />
              </div>
              <Input
                id="address"
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street"
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="city">City</Label>
                  <FieldStatusIndicator fieldName="city" status={fieldStatus.city} />
                </div>
                <Input
                  id="city"
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="state">State</Label>
                  <FieldStatusIndicator fieldName="state" status={fieldStatus.state} />
                </div>
                <Select 
                  value={formData.state || ''} 
                  onValueChange={(v) => handleChange('state', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <FieldStatusIndicator fieldName="zip_code" status={fieldStatus.zip_code} />
                </div>
                <Input
                  id="zip_code"
                  value={formData.zip_code || ''}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  placeholder="12345"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Employment Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              Employment Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="job_title">Job Title</Label>
                  <FieldStatusIndicator fieldName="job_title" status={fieldStatus.job_title} />
                </div>
                <Input
                  id="job_title"
                  value={formData.job_title || ''}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="department">Department</Label>
                  <FieldStatusIndicator fieldName="department" status={fieldStatus.department} />
                </div>
                <Input
                  id="department"
                  value={formData.department || ''}
                  onChange={(e) => handleChange('department', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="hire_date">Hire Date</Label>
                  <FieldStatusIndicator fieldName="hire_date" status={fieldStatus.hire_date} />
                </div>
                <Input
                  id="hire_date"
                  type="date"
                  value={formData.hire_date || ''}
                  disabled
                  className="bg-slate-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="employment_status">Employment Status</Label>
                <Input
                  id="employment_status"
                  value={formData.employment_status?.replace('_', ' ').toUpperCase() || ''}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex justify-end gap-4 pb-8"
      >
        <Button variant="outline" onClick={loadData} disabled={saving}>
          Reset
        </Button>
        
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
        
        {!formData.profile_confirmed && (
          <Button 
            onClick={handleConfirmProfile}
            disabled={saving}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirm Profile
          </Button>
        )}
      </motion.div>
    </div>
  );
}